<template>
  <HKLCollapse
    :title="$t('vendor.contacts')"
    class="vendor-contacts"
  >
    <div
      slot="content"
      class="vendor-contacts__table"
    >
      <div class="row">
        <label class="column">{{ $t('vendor.allContacts') }}</label>
        <div
          v-if="canManageAllContacts"
          class="column"
        >
          <div class="row action-bar align-right">
            <HKLButton
              v-if="!editStatus"
              color="orange"
              action="edit"
              @click="chnageEditStatus"
            />
            <template v-if="editStatus">
              <HKLButton
                color="darkblue"
                action="cancel"
                @click="cancel"
              />
              <HKLButton
                color="orange"
                action="save"
                @click="save"
              />
            </template>
          </div>
        </div>
      </div>
      <HKLTable
        ref="vendorContactsTable"
        :dataSource="dataSrc"
        :columns="columns"
        :filterBar="false"
        :onFilter="false"
      />
      <LinkCcdContactButton
        v-if="editStatus && canManageAllContacts"
        :queryParams="linkBtnQueryParams"
      />
    </div>
  </HKLCollapse>
</template>

<script>
import lodash from 'lodash'
import HKLCollapse from '@/controls/Collapse'
import HKLTable from '@/controls/Table/HKLTable'
import HKLButton from '@/controls/Button/HKLButton'
import LinkCcdContactButton from '@/controls/LinkCcdContactButton'

import { getCompanyContacts, updateCompanyContacts } from '@/api/companies/companyContact.request'
import { contactTypes } from '@/api/users/consts'
import VendorECircularCheckboxCell from './VendorECircularCheckboxCell'
import VendorContactNameCell from './VendorContactNameCell'
import { mapGetters } from 'vuex'
import { ROUTER_PATHS } from '@/constants/router'
import { removeContactsFromCompany } from '@/api/companies/request'
import { aclHelper, aclPermsType, contractorPermsTypes } from '@/utils/aclHelper'

export default {
  components: {
    HKLCollapse,
    HKLTable,
    HKLButton,
    LinkCcdContactButton,
  },
  inject: [
    'getCompanyInfo',
  ],
  provide () {
    return {
      getEdiStatus: () => this.editStatus,
      getUpdateAllContactsAccess: () => this.canManageAllContacts,
    }
  },
  data () {
    return {
      originContacts: [],
      contacts: [],
      pendingContacts: new Map(),
      pendingDeleteContactIds: new Set(),
      editStatus: false,
    }
  },
  computed: {
    ...mapGetters('user', ['isEngineer', 'isTenant', 'isContractor', 'isContractorAdmin']),
    ...mapGetters('regions', ['enableEcircularFeature']),
    linkBtnQueryParams () {
      const routeMap = {
        isEngineer: ROUTER_PATHS.VENDOR_DETAIL,
        isContractor: 'vendorSettings',
        isContractorAdmin: 'vendorSettings',
      }
      const key = Object.keys(routeMap).find(key => this[key])
      return { companyId: this.companyId, from: routeMap[key] }
    },
    companyId () {
      return this.getCompanyInfo().id
    },
    columns () {
      const columns = [
        {
          title: this.$t('company.contactTable.name'),
          dataIndex: 'name',
          emptyValue: this.$t('emptyValue'),
          component: {
            name: VendorContactNameCell,
          },
        },
        {
          title: this.$t('company.contactTable.title'),
          dataIndex: 'jobTitle',
          emptyValue: this.$t('emptyValue'),
        },
        {
          title: this.$t('general.phone'),
          dataIndex: 'phone',
          emptyValue: this.$t('emptyValue'),
        },
        {
          title: this.$t('general.email'),
          dataIndex: 'email',
          emptyValue: this.$t('emptyValue'),
        },
      ]
      if (this.enableEcircularFeature) {
        const vendorCircular = {
          title: this.$t('settings.user.vendor.revECircular'),
          dataIndex: 'isVendorCircular',
          class: 'rev-ec-col',
          component: {
            name: VendorECircularCheckboxCell,
            listeners: {
              onChange: this.onChangeReceiveECircular,
              onDelete: this.onDeleteContact,
            },
          },
        }
        columns.push(vendorCircular)
      }
      return columns
    },
    dataSrc () {
      const result = this.contacts.filter(item => !lodash.isEmpty(item.ccdContact))
        .map((item) => {
          const getContactsByType = (data, type) => {
            const result = data.filter(item => item.type === type)
              .map(item => item.value).join(', ')
            return result || null
          }
          const ccdContact = item.ccdContact
          const phone = getContactsByType(ccdContact.contacts, contactTypes.PHONE_NUMBER)
          const email = getContactsByType(ccdContact.contacts, contactTypes.EMAIL)
          return {
            id: item.id,
            name: ccdContact.name,
            jobTitle: item.title,
            phone,
            email,
            isVendorCircular: item.isVendorCircular,
            ccdContact,
          }
        })
      return result
    },
    isEditModeByQueryRoute () {
      const mode = lodash.get(this, '$route.query.mode', null)
      return mode === 'edit'
    },
    canManageAllContacts () {
      const canVendorUpdateAllContacts = this.isContractorAdmin || aclHelper.getAccess(contractorPermsTypes.settings.updateVendorContacts)
      const canEngineerEdit = aclHelper.getAccess(aclPermsType.vendorECircular.manageECircularContact)
      return canEngineerEdit || canVendorUpdateAllContacts
    },
  },
  watch: {
    companyId: async function (value) {
      if (!value) return
      await this.refreshContacts()
    },
  },
  mounted () {
    this.editStatus = this.isEditModeByQueryRoute
  },
  methods: {
    async refreshContacts () {
      this.contacts = await getCompanyContacts(this.companyId)
      this.originContacts = lodash.cloneDeep(this.contacts)
    },
    resetContacts () {
      this.contacts = lodash.cloneDeep(this.originContacts)
    },
    onChangeReceiveECircular (id, value) {
      this.pendingContacts.set(id, value)
    },
    onDeleteContact (ccdId, recordId) {
      this.pendingDeleteContactIds.add(ccdId)
      const delIndex = this.contacts.findIndex(item => item.id === recordId)
      this.pendingContacts.delete(recordId)
      this.contacts.splice(delIndex, 1)
    },
    chnageEditStatus () {
      this.editStatus = true
    },
    async save () {
      const updateingIds = []
      this.pendingContacts.forEach(function (value, key) {
        updateingIds.push({ id: key, isVendorCircular: value })
      })
      if (!lodash.isEmpty(updateingIds)) {
        await updateCompanyContacts(this.companyId, updateingIds)
      }
      const deletingIds = Array.from(this.pendingDeleteContactIds)
      if (!lodash.isEmpty(deletingIds)) {
        await removeContactsFromCompany(this.companyId, { ccdContactIds: deletingIds })
      }
      this.fallback()
      await this.refreshContacts()
    },
    async cancel () {
      this.fallback()
      await this.resetContacts()
    },
    fallback () {
      this.removeUrlEditMode()
      this.refreshToInitState()
    },
    refreshToInitState () {
      this.editStatus = false
      this.pendingContacts = new Map()
      this.pendingDeleteContactIds = new Set()
    },
    removeUrlEditMode () {
      this.$router.replace({ query: null })
    },
  },
}
</script>

<style lang="scss" scoped>
.vendor-contacts {
  margin-bottom: 10px;
  &__table {
    margin-top: 10px;
    ::v-deep .hkl-table {
      margin: 5px 0;
      .rev-ec-col .hkl-table__header-title {
        text-align: center;
      }
    }
  }
  .action-bar {
    button {
      margin-left: 10px;
    }
  }
}
</style>
